import { useEffect, useState } from 'react'
import { subscribeToToasts, type ToastEvent } from '../../utils/toastBus'

export type ToastItem = ToastEvent & { id: string; createdAt: number; ttl: number }

function clsx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ')
}

function getStylesByType(type: ToastEvent['type']) {
  switch (type) {
    case 'success':
      return {
        container: 'bg-emerald-50 border-emerald-200',
        title: 'text-emerald-700',
        desc: 'text-emerald-700',
        icon: 'ri-check-line text-emerald-600',
        progress: 'bg-emerald-200'
      }
    case 'error':
      return {
        container: 'bg-red-50 border-red-200',
        title: 'text-red-700',
        desc: 'text-red-700',
        icon: 'ri-error-warning-line text-red-600',
        progress: 'bg-red-200'
      }
    case 'warning':
      return {
        container: 'bg-amber-50 border-amber-200',
        title: 'text-amber-800',
        desc: 'text-amber-800',
        icon: 'ri-alert-line text-amber-600',
        progress: 'bg-amber-200'
      }
    default:
      return {
        container: 'bg-blue-50 border-blue-200',
        title: 'text-blue-700',
        desc: 'text-blue-700',
        icon: 'ri-information-line text-blue-600',
        progress: 'bg-blue-200'
      }
  }
}

export default function ToastProvider() {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  useEffect(() => {
    const unsubscribe = subscribeToToasts((event) => {
      const ttl = event.type === 'error' || event.type === 'warning' ? 5000 : 3000
      const item: ToastItem = {
        ...event,
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        createdAt: Date.now(),
        ttl
      }
      setToasts((prev) => [...prev, item])
    })
    return unsubscribe
  }, [])

  // Auto-dismiss
  useEffect(() => {
    if (toasts.length === 0) return
    const timers = toasts.map((t) =>
      setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x.id !== t.id))
      }, t.ttl)
    )
    return () => timers.forEach(clearTimeout)
  }, [toasts])

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 w-[92vw] max-w-sm" aria-live="polite" aria-atomic="true">
      {toasts.map((toast) => {
        const s = getStylesByType(toast.type)
        return (
          <div
            key={toast.id}
            role="status"
            className={clsx(
              'rounded-2xl border shadow-sm p-4 pr-10 relative overflow-hidden animate-[fadeIn_.2s_ease-out] backdrop-blur-[2px]',
              s.container
            )}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <i className={clsx('text-lg', s.icon)}></i>
              </div>
              <div className="flex-1">
                {toast.title && <div className={clsx('text-sm font-semibold', s.title)}>{toast.title}</div>}
                <div className={clsx('text-sm leading-snug', s.desc)}>{toast.message}</div>
              </div>
              <button
                aria-label="Fechar"
                onClick={() => setToasts((prev) => prev.filter((x) => x.id !== toast.id))}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/60 hover:bg-white/80 flex items-center justify-center"
              >
                <i className="ri-close-line text-gray-600 text-sm"></i>
              </button>
            </div>
            {/* progress bar */}
            <div className={clsx('absolute left-0 bottom-0 h-1', s.progress)} style={{ width: '100%', opacity: 0.7 }} />
          </div>
        )
      })}
    </div>
  )
}
